import React, { useState, useEffect, useMemo } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { 
  TextField, 
  Button, 
  Paper, 
  Tabs, 
  Tab, 
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Checkbox
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Title from '../../components/Title';
import UserTypeSelect from '../../containers/UserTypeSelect';
import { InputChangeEvent } from '../../resources/utils';
import { sendPushAsync } from '../../redux/slices/usersSlice';
import { SendPushBody, UserType } from '../../resources/types/usersTypes';
import PushTemplatesTab from './PushTemplatesTab';
import PushHistoryTab from './PushHistoryTab';
import ScheduledPushesTab from './ScheduledPushesTab';
import { pushTemplatesApi } from '../../redux/api/pushTemplatesApi';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import { selectCities } from '../../redux/slices/citiesSlice';

const PREFIX = 'PushesPage';

const classes = {
  header: `${PREFIX}-header`,
  wrapper: `${PREFIX}-wrapper`,
  submit: `${PREFIX}-submit`
};

const Root = styled('div')({
  [`& .${classes.header}`]: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 20
  },
  [`& .${classes.wrapper}`]: {
    maxWidth: 400
  },
  [`& .${classes.submit}`]: {
    marginTop: 20
  }
});

const PushesPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const admins = useSelector(selectAdmins);
  const cities = useSelector(selectCities);

  const [tabValue, setTabValue] = useState(0);
  const [text, setText] = useState('');
  const [userType, setUserType] = useState<UserType>('');
  const [confirmDialog, setConfirmDialog] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(3);
  const [isScheduled, setIsScheduled] = useState<boolean>(false);
  const [scheduledTime, setScheduledTime] = useState<string>('');

  const currentCityName = useMemo(() => {
    const city = cities.items.find(c => c._id === admins.currentCity);
    return city?.cityName || t('pushes.notSelected');
  }, [cities.items, admins.currentCity, t]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (confirmDialog && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [confirmDialog, countdown]);

  const getUserTypeLabel = (type: UserType) => {
    if (!type) return t('pushes.allUsersLower');
    const labels: Record<string, string> = {
      'vip': t('pushes.vipUsersLower'),
      'normal': t('pushes.normalUsersLower')
    };
    return labels[type] || type;
  };

  const submit = () => {
    if (!text) {
      alert(t('pushes.enterText'));
    } else {
      setConfirmDialog(true);
      setCountdown(3);
    }
  };

  const handleConfirm = async () => {
    const body: SendPushBody = { 
      text,
      realSend: true
    };

    if (userType) body.userType = userType;
    if (isScheduled && scheduledTime) {
      body.sendAfter = new Date(scheduledTime).toISOString();
    }

    await dispatch(sendPushAsync(body, () => {
      // Success callback - only runs if API call succeeds
      dispatch(pushTemplatesApi.util.invalidateTags(['PushHistory']));
      alert(isScheduled ? t('pushes.pushScheduled') : t('pushes.pushSentSuccess'));
      setText('');
      setUserType('');
      setIsScheduled(false);
      setScheduledTime('');
      setConfirmDialog(false);
    }));
  };

  const handleCancel = () => {
    setConfirmDialog(false);
    setCountdown(3);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Root>
      <Paper sx={{ border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', borderRadius: 2, padding: '20px' }}>
        <div className={classes.header}>
          <Title>{t('pushes.title')}</Title>
        </div>

        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          sx={{
            mb: 3,
            '& .MuiTab-root': {
              fontWeight: 500,
              fontSize: '0.95rem'
            },
            '& .Mui-selected': {
              color: theme.palette.mode === 'dark' ? '#5073F8 !important' : '#424242 !important'
            },
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.mode === 'dark' ? '#5073F8' : '#424242'
            }
          }}
        >
          <Tab label={t('pushes.tabSend')} />
          <Tab label={t('pushes.tabScheduled')} />
          <Tab label={t('pushes.tabTemplates')} />
          <Tab label={t('pushes.tabHistory')} />
        </Tabs>

        {tabValue === 0 && (
          <Box>
            <div className={classes.wrapper}>
              <UserTypeSelect
                allOption
                value={userType}
                onChange={(e: InputChangeEvent) =>
                  setUserType(e.target.value as UserType)
                }
              />
            </div>
            <TextField
              rows={12}
              fullWidth
              multiline
              required
              label={t('pushes.textLabel')}
              margin='normal'
              value={text}
              onChange={(e: InputChangeEvent) => setText(e.target.value)}
            />
            <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
              <Typography variant='body2'>{t('pushes.scheduledSend')}</Typography>
              <input
                type='checkbox'
                checked={isScheduled}
                onChange={(e) => {
                  setIsScheduled(e.target.checked);
                  if (!e.target.checked) setScheduledTime('');
                }}
              />
            </Box>
            {isScheduled && (
              <TextField
                fullWidth
                type='datetime-local'
                label={t('pushes.sendTime')}
                margin='normal'
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                inputProps={{ step: '1' }}
                InputLabelProps={{ shrink: true }}
                sx={{ mt: 2 }}
              />
            )}
            <Button
              className={classes.submit}
              color='primary'
              variant='contained'
              onClick={submit}
            >
              {isScheduled ? t('pushes.scheduleBtn') : t('pushes.sendBtn')}
            </Button>
          </Box>
        )}

        {tabValue === 1 && (
          <ScheduledPushesTab />
        )}

        {tabValue === 2 && (
          <PushTemplatesTab />
        )}

        {tabValue === 3 && (
          <PushHistoryTab />
        )}
      </Paper>

      <Dialog
        open={confirmDialog}
        onClose={handleCancel}
      >
        <DialogTitle>{t('pushes.confirmation')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.main', color: 'white', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              🏙️ {t('pushes.cityLabel', { city: currentCityName })}
            </Typography>
          </Box>
          <Typography>
            {t('pushes.confirmSendMessage', { userType: getUserTypeLabel(userType) })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>
            {t('pushes.cancel')}
          </Button>
          <Button 
            color="primary" 
            variant="contained"
            onClick={handleConfirm}
            disabled={countdown > 0}
          >
            {countdown > 0 ? t('pushes.yesCountdown', { seconds: countdown }) : t('pushes.yes')}
          </Button>
        </DialogActions>
      </Dialog>
    </Root>
  );
};

export default PushesPage;
